import {AfterViewInit, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { OptionService } from "../option/option.service";
import {Donation} from "../donation/donation";
import {TranslateService} from "@ngx-translate/core";
import {DecimalPipe} from "@angular/common";
import { UrlService } from '../url/url.service';

@Component({
    selector: 'rythm-target',
    templateUrl: './rythm_target.component.html',
    providers: [DecimalPipe]
})
export class RythmTargetComponent implements OnInit, AfterViewInit {
    /**
     * the current donation object
     */
    @Input() donation: Donation;

    /**
     * The form group for validation
     */
    @Input() form: FormGroup;

    /**
     * The current size fo the widget
     */
    @Input() size: string;

    /**
     * NUmber of donation targets from which on we sho a dropdown
     */
    private static DONATION_TARGETS_DROPDOWN_TARGET_MINCOUNT = 4;

    /**
     * List of donationtargets
     * @type {array}
     */
    donationTargets = this.optionService.getDonationTargets();

    /**
     * Stores the configuration of the donation_rythm
     *
     * @type {array}
     */
    donationRythm = this.optionService.getOption('donation_rhythm');

    /**
     * Classes for the recurring buttons
     * @type {{}}
     */
    recurringWrapperClasses : any = {};

    /**
     * Decides to show a rhythm wrapper or not
     * @type {boolean}
     */
    showRhythmWrapper : boolean = true;

    /**
     * Toggle for the target wrapper/section
     * @type {boolean}
     */
    showTargetWrapper: boolean = true;

    /**
     * Checks if the target values are to long to show them inside buttons
     * @type {boolean}
     */
    targetsTooLong: boolean = false;

    /**
     * Should the input of the donation target a textfield?
     * @type {boolean}
     */
    targetAsTextfield: boolean = false;

    /**
     * All available recurring rhythms
     *
     * @type {[string,string,string,string]}
     */
    public static allRecuringDonationRhythms : any = ['monthly', 'quarterly', 'halfyearly', 'yearly'];

    /**
     * List of recurring donations rythms that are available
     */
    donationRythmsRecurring: any;

    /**
     * Are we forced to display donation targets as buttons?
     * @type {boolean}
     */
    forceDonationButtons: boolean = false;

    /**
     * Holds the type of the project
     */
    projectType: string = null;

    /**
     * Textkey for the main label of the component
     */
    rythmLabelTextkey: string = null;

    /**
     * In some cases we want other labels for the recurring buttons
     */
    recurringAdditionalTextkey: string = "";

    /**
     * should there be a null target
     */
    showNullTarget: boolean = false;

    /**
     * show the input for the donation target
     */
    showTargetInput: boolean = false;

    /**
     * model for the radio buttons
     */
    radioButtonTarget: string = null;

    /**
     * textfield for the free input readonly flag
     */
    freeInputReadonly: boolean = false;

    /**
     * The constructor
     * @param optionService
     * @param translate
     * @param decimalPipe
     */
    constructor(private optionService: OptionService, private translate: TranslateService, private decimalPipe: DecimalPipe, private cdRef: ChangeDetectorRef, private urlService: UrlService) {
        this.setDonationRythmsConcurring();
    }

    /**
     * Initialize the component
     */
    public ngOnInit(): any {
        // get the type of the project
        this.projectType = this.optionService.getProjectType();

        this.form.addControl('donation_rhythm', new FormControl());
        this.form.addControl('donation_target', new FormControl('', [Validators.required]));
        this.form.addControl('donation_rhythm_recurring', new FormControl());
        this.form.addControl('donation_target_selection', new FormControl());

        // if presetting a target is forbidden, there has to be a null target
        this.showNullTarget = this.optionService.getOption('has_target_no_selection');

        // check if we need to preset the donation target with a "choose" value
        this.checkDropdownValue();

        // check if we want to force the donation targets as buttons
        this.forceDonationButtons = this.optionService.getOption('has_force_donation_target_buttons');

        // checks if the target wrapper should be visible or not
        this.showTargetWrapper = this.donationTargets.length > 1 ||
            this.optionService.getOption('has_show_single_target') ||
            this.optionService.getOption('has_donationtarget_textfield');

        // Check if there are any presets and so we have to mark the fields as touched
        if(this.donation.rythm !== null) {
            this.form.controls['donation_rhythm'].setValue(this.donation.rythm);
        }
        this.form.controls['donation_rhythm_recurring'].setValue(this.donation.recurringRythm);

        // preset the donation_target in the form
        if(this.donation.target !== null && this.donation.target !== '') {
            this.radioButtonTarget = this.donation.target;
            this.form.controls['donation_target'].setValue(this.donation.target);

            // preset a button if possible 
            for (let target of this.donationTargets) {
                if (target.donation_target === this.donation.target) {
                    this.form.controls['donation_target_selection'].setValue(target.donation_target);
                    break;
                }
            }
        }
        // preset the radio button
        if (this.form.controls['donation_target_selection'].value) {
            this.radioButtonTarget = this.form.controls['donation_target_selection'].value;
        }

        // if we have a textfield and a target, but not a target selection, we have to set the target selection to other
        // target is coming from the url parameter
        if (this.donation.target !== null && this.donation.target !== '' && this.form.controls['donation_target_selection'].value === null) {
            console.log('here');
            this.form.controls['donation_target_selection'].setValue('other');
            
            // if it is a fixed target we set the input to readonly
            this.freeInputReadonly = !!this.urlService.findGetParameter(this.urlService.PARAMETER_TARGET_PRESET_FIX);
        }

        if(this.donationTargets.length == 0 && this.optionService.getOption('has_donationtarget_textfield') !== true) {
            // generate a generic donation target
            console.log("No donation target available. Add a generic one");
            let that = this;
            this.translate.get('target.default').subscribe((res: string) => {
                let defaultTarget = {donation_target: res, id: 0};
                that.donation.target = <string>defaultTarget.donation_target;
                this.form.controls['donation_target'].setValue(that.donation.target);
                that.donationTargets.push(defaultTarget);
            });
        }
        else if(this.optionService.getOption('has_donationtarget_textfield') === true) {
            this.targetAsTextfield = true;
        }

        // if we have a single target, and already a selected target we do not show the textfield
        if(this.donationTargets.length == 1 && this.donation.target !== null && this.donation.target !== '' && this.optionService.getOption('has_show_single_target') === true) {
            this.targetAsTextfield = false;
        }
        

        // set classes to the recurring wrapper
        this.recurringWrapperClasses = {
            'medium-8': this.donationRythm.one_time == true,
            'medium-offset-4': this.donationRythm.one_time == true,
            'large-6': this.donationRythm.one_time == true,
            'large-offset-6' : this.donationRythm.one_time == true
        };

        // show rhytmwrapper or not
        this.showRhythmWrapper = this.donationRythmsRecurring.length > 0 || this.donationRythm.one_time == false;

        // set the textkey for the label of the rythm
        if(this.projectType === 'membership') {
            this.rythmLabelTextkey = 'rythm.label.membership';
            this.recurringAdditionalTextkey = '.membership';
        }
        else {
            if (this.donationRythm.one_time == false && this.donationRythmsRecurring.length == 1) {
                this.rythmLabelTextkey = 'rythm.label_single';
            } else {
                this.rythmLabelTextkey = 'rythm.label';
            }
        }

        // save internal targetid if we have one.
        this.updateTargetId();

        // show free input
        if (this.form.controls['donation_target_selection'].value === 'other') {
            this.showTargetInput = true;
        }
    }

    /**
     * Method is fired after the DOM of the component is loeaded
     */
    public ngAfterViewInit(): void {
        // get all available donation targets
        let targets = document.querySelectorAll(".donation-target");

        let that = this;
        // only check the height/width if we have more than one donation target und do not already show an dropdown
        if(this.forceDonationButtons != true &&
            targets.length > 1 &&
            targets.length < RythmTargetComponent.DONATION_TARGETS_DROPDOWN_TARGET_MINCOUNT) {
            let parent = document.querySelector(".donation-target-row");
            let expectedWidth = parent.clientWidth / targets.length;
            // we expect a maximum height of 50px (as defined in the css)
            let expectedHeight = 50;

            // check each doonation target if the text is higher or wider as we expect it
            for(let i=0; i<targets.length; i++) {
                // let radio = $(this).find("label.radio");
                let radio = targets[i].querySelector("label.radio");

                if (radio && (radio.clientHeight > expectedHeight || radio.clientWidth > expectedWidth)) {
                    // the radio is heigher or wider, lets switch to an dropdown
                    that.targetsTooLong = true;
                    this.checkDropdownValue();
                }
            }
        }
    }

    /**
     * Check if we have to set the target selection to "choose" or not
     */
    checkDropdownValue() {
        if (this.optionService.getOption('has_donationtarget_textfield') || (this.showDonationTargetAsDropdown() && this.showNullTarget)) {
            if ((this.donation.target === null || this.donation.target === '') && this.form.controls['donation_target_selection'].value === null) {
                this.form.controls['donation_target_selection'].setValue('choose');
            }
        }
    }

    /**
     * Returns if the block for the donation rhythm is valid or not
     *
     * @returns {boolean}
     */
    private rhythmValid(): boolean {
        if(this.donation.rythm == 'one_time' || this.donation.rythm == null) {
            return this.form.controls['donation_rhythm'].touched  == false || this.form.controls['donation_rhythm'].valid == true
        }
        else {
            return this.form.controls['donation_rhythm_recurring'].touched == false || this.form.controls['donation_rhythm_recurring'].valid == true
        }
    }

    /**
     * Returns if the target is valid
     * @returns {boolean}
     */
    private targetValid(): boolean {
        return this.form.controls['donation_target'].touched == false || this.form.controls['donation_target'].valid == true;
    }

    /**
     * Returns a list of the actived concurring donations rythems
     * @returns {Array}
     */
    private setDonationRythmsConcurring(): any {
        this.donationRythmsRecurring = [];
        if(this.donationRythm['monthly']) {
            this.donationRythmsRecurring.push('monthly');
        }
        if(this.donationRythm['quarterly']) {
            this.donationRythmsRecurring.push('quarterly');
        }
        if(this.donationRythm['halfyearly']) {
            this.donationRythmsRecurring.push('halfyearly');
        }
        if(this.donationRythm['yearly']) {
            this.donationRythmsRecurring.push('yearly');
        }
    }

    /**
     * Presets the first item of the available recurring items
     */
    private presetRecurringRhythm(): void {
        for(let i=0; i < RythmTargetComponent.allRecuringDonationRhythms.length; i++) {
            if(this.donationRythm[RythmTargetComponent.allRecuringDonationRhythms[i]]) {
                this.donation.recurringRythm = RythmTargetComponent.allRecuringDonationRhythms[i];
                this.form.controls['donation_rhythm_recurring'].setValue(this.donation.recurringRythm);
                this.form.controls['donation_rhythm_recurring'].updateValueAndValidity();
                break;
            }
        }
    }

    /**
     * Returns a list of classe for the donation target buttons
     * @returns {any}
     */
    private getDonationTargetClasses(): string {
        const targets = this.targetAsTextfield ? this.donationTargets.length + 1 : this.donationTargets.length;
        switch(targets) {
            case 1:
                return "small-12";
            case 2:
                return "small-6";
            case 3:
                return "medium-4 small-12";
            default:
                if(this.showRhythmWrapper == true) {
                    return "small-12 medium-6";
                }
                else {
                    return "small-12 medium-4";
                }
        }
    }

    /**
     * returns if the donation targets should be shown as dropdown or not
     * @returns {boolean}
     */
    private showDonationTargetAsDropdown(): boolean {
        // one less if we have a textfield
        const maxCount = this.targetAsTextfield ? RythmTargetComponent.DONATION_TARGETS_DROPDOWN_TARGET_MINCOUNT - 1 : RythmTargetComponent.DONATION_TARGETS_DROPDOWN_TARGET_MINCOUNT
        return this.forceDonationButtons != true &&
            (
                this.targetsTooLong ||
                (this.donationTargets.length >= maxCount)
            );
    }

    private getDonationRythmsRecurring(): any {
        if(this.donationRythm.one_time == true) {
            return [null].concat(this.donationRythmsRecurring);
        }
        else {
            return this.donationRythmsRecurring;
        }
    }

    /**
     * Returns if the donation rythm should be shown as dropdown or not
     *
     * @returns {boolean}
     */
    private showDonationRythmAsDropDown(): boolean {
        // case 0: no one_time and no targets
        if(this.size != "small" && this.showTargetWrapper === false && this.donationRythm.one_time == false) {
            return false;
        }

        // case 1: one_time is activates and we have more than one rythm
        if(this.donationRythm.one_time == true && this.donationRythmsRecurring.length > 2) {
            return true;
        }

        // case 2: at least one target and more than one rythm
        if(this.donationTargets.length > 0 && this.donationRythmsRecurring.length > 2) {
            return true;
        }

        // case 3: small screens and more than two rythms
        if(this.size == "small" && this.donationRythmsRecurring.length > 2) {
            return true;
        }

        return false;
    }

    /**
     * Returns a list of placeholders for the recurring label
     */
    getRecurringLabelPlaceholders(): any {
        const amount = isNaN(this.donation.amount)? parseFloat(this.donation.amount + '') : this.donation.amount;
        return {
            currencySymbol: this.donation.currencySymbol,
            donationAmount: this.decimalPipe.transform(amount, '1.0-0'),
            donationAmountx12: this.decimalPipe.transform(amount * 12, '1.0-0'),
            donationAmountx6: this.decimalPipe.transform(amount * 6, '1.0-0'),
            donationAmountx3: this.decimalPipe.transform(amount * 3, '1.0-0')
        }
    }

    /**
     * Updates the internal target id if there is one set, based on the selected donation target.
     */
    updateTargetId(): void {
        let id;
        for(let target of this.donationTargets) {
            if(target.donation_target === this.donation.target && target.hasOwnProperty("internal_id")) {
                id = target.internal_id;
                break;
            }
        }
        this.donation.targetInternalId = id;
    }

    /**
     * Handle changes on the target selection
     * @param event 
     */
    onTargetChange(value) {   
        if (value === 'other') {
            // if the target is other, show the input field and clear the target
            this.donation.target = null;
            this.form.controls['donation_target'].setValue(this.donation.target);
            this.showTargetInput = true;          
        } else if (value === 'choose') {
            // if the target is choose, hide the input field and clear the target
            this.donation.target = null;
            this.form.controls['donation_target'].setValue(this.donation.target);
            this.showTargetInput = false;
        } else {
            // if the target is a valid target, set the target and hide the input field
            this.donation.target = value;
            this.form.controls['donation_target'].setValue(this.donation.target);
            this.showTargetInput = false;
            this.updateTargetId();
        }

        this.cdRef.detectChanges();
    }
}
